import React, {useEffect, useRef, useState} from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import OeSelect from './OeSelect';
import {fmt} from './format';
import {advancePercentOf, parseAmount, restAmount} from './payment';

// Zahlungsschritt des Auftrags-Editors (Original `#paybar` /
// `renderPaymentBar` 2706–2774 und `renderAdvanceBox` 2774–2799).
//
// Hier stehen bewusst nur die beiden Felder, die wirklich zu tun sind:
// Auftragsart und Kunden-Bestellnummer. Alles andere zur Zahlung
// (Zahlungsbedingung, Beträge) steht im Beleg-Kopf. Ausnahme ist die Warnung,
// dass die Zahlungsbedingung des Auftrags vom Kundenstamm abweicht — die
// gehört hierher, weil sie eine Entscheidung verlangt.
//
// Alle Eingaben bleiben vorgemerkt (`PENDING_PAYTYPE`, `PENDING_ADVANCE`,
// `PENDING_CUSTORDERNO`) und gehen erst mit „Speichern" nach weclapp.

/**
 * @param {string} mode
 * @param {object} order - Beleg aus `/load`.
 * @param {{payType: ?string, advance: ?number, custOrderNo: ?string}} pending
 * @param {boolean} canEdit
 * @param {(action: object) => void} dispatch
 */
export default function PaymentBar({mode, order, pending, canEdit, dispatch}) {
    const p = (mode === 'order' && order) ? order.payment : null;

    const advanceRef = useRef(null);
    const [advText, setAdvText] = useState('');

    const cur = p ? (pending.payType || p.current) : null;
    const eff = p
        ? (pending.advance != null ? pending.advance : (p.advanceCurrent != null ? p.advanceCurrent : null))
        : null;

    // Den Betrag nur nachziehen, solange niemand darin schreibt — sonst
    // überschriebe die Formatierung die laufende Eingabe.
    useEffect(() => {
        if (document.activeElement === advanceRef.current) {
            return;
        }
        setAdvText(eff != null ? fmt(eff) : '');
    }, [eff]);

    if (!p) {
        return null;
    }

    const curLabel = (p.labels && p.labels[cur]) || cur || '—';
    const changed = !!(pending.payType && pending.payType !== p.current);
    const termName = p.termName || p.customerTermName || '—';
    const mismatch = !!(p.hasOrderTerm && p.customerTermName && !p.matchesCustomer);
    const needSet = !!(p.suggested && p.suggested !== cur);

    const curNo = pending.custOrderNo != null ? pending.custOrderNo : (order.customerOrderNumber || '');
    const typeOptions = Object.keys(p.labels || {}).map((k) => ({value: k, label: p.labels[k]}));

    const setPending = (patch) => dispatch({type: 'set-pending', patch});

    const onType = (value) => {
        const next = value === p.current ? null : value;
        // Eine Anzahlungssumme gibt es nur bei der Auftragsart „Anzahlung".
        const advance = (next || p.current) === 'ADVANCE_PAYMENT' ? pending.advance : null;
        setPending({payType: next, advance});
    };

    const onAdvInput = (text) => {
        setAdvText(text);
        setPending({advance: text.trim() === '' ? null : parseAmount(text)});
    };

    const gross = p.gross || 0;
    const effPct = advancePercentOf(gross, eff);
    const rest = restAmount(gross, eff);
    const advChanged = pending.advance != null && pending.advance !== p.advanceCurrent;

    return (
        <div className="fbar" id="oe-paybar" style={{display: 'flex'}}>
            {mismatch ? (
                <div style={{width: '100%'}} className="poinfo">
                    <span style={{color: 'var(--warn)'}}>
                        ⚠ Zahlungsbedingung weicht vom Kundenstamm ab — Auftrag: „{termName}",
                        {' '}Kunde: „{p.customerTermName}"
                    </span>
                    <HelpTooltip id="oe-pay-mismatch" placement="bottom" size={0.7}>
                        Am Auftrag steht eine andere Zahlungsbedingung als im Kundenstamm. Das kann so gewollt sein
                        (Sonderabsprache) — geändert wird die Zahlungsbedingung nur in weclapp, nicht hier.
                    </HelpTooltip>
                </div>
            ) : null}

            <div className="payfields">
                <div className="pf">
                    <span className="pf-l">
                        ✎ Auftragsart
                        <HelpTooltip
                            id="oe-pay-type"
                            placement="bottom"
                            size={0.7}
                            detail={<>
                                <b>Standard</b> (Rechnung nach Lieferung), <b>Vorkasse</b> (100 % im Voraus) oder
                                <b> Anzahlung</b> (Teilbetrag im Voraus mit eigener Anzahlungsrechnung).
                            </>}
                        >
                            Steuert, wie weclapp den Auftrag abrechnet. Die Auswahl wird hier nur vorgemerkt und
                            erst mit „Speichern" gesetzt.
                        </HelpTooltip>
                    </span>
                    <OeSelect
                        className="hl-field pf-in"
                        value={cur || ''}
                        options={typeOptions}
                        onChange={onType}
                        disabled={!canEdit}
                        ariaLabel="Auftragsart"
                    />
                    <span className="pf-hint">laut Zahlungsbedingung „{termName}"</span>
                </div>

                <div className="pf">
                    <span className="pf-l">
                        ✎ Kunden-Bestellnummer
                        <HelpTooltip id="oe-pay-custno" placement="bottom" size={0.7}>
                            Die Bestellnummer des Kunden („Ihre Best-Nr."). Sie erscheint auf
                            Auftragsbestätigung und Rechnung. Höchstens 60 Zeichen; gespeichert wird sie erst mit
                            „Speichern".
                        </HelpTooltip>
                    </span>
                    <input
                        className="hl-field pf-in"
                        type="text"
                        value={curNo}
                        maxLength={60}
                        autoComplete="off"
                        disabled={!canEdit}
                        aria-label="Kunden-Bestellnummer"
                        placeholder="falls vorhanden eintragen…"
                        onChange={(e) => setPending({
                            custOrderNo: e.target.value === (order.customerOrderNumber || '') ? null : e.target.value,
                        })}
                    />
                    <span className="pf-hint">steht oft in der Kundenbemerkung</span>
                </div>
            </div>

            {needSet && canEdit ? (
                <OeTip
                    tip={'Übernimmt die Auftragsart, die zur Zahlungsbedingung gehört. Vorgemerkt — gespeichert wird erst mit „Speichern".'}
                    detail={p.suggestedSource === 'regel'
                        ? 'Die Auftragsart ist aus dem Namen der Zahlungsbedingung abgeleitet, weil dafür keine Zuordnung gepflegt ist.'
                        : undefined}
                >
                    <button
                        type="button"
                        className="actbtn"
                        onClick={() => setPending({payType: p.suggested})}
                    >
                        ✓ Vorschlag „{p.suggestedLabel}" übernehmen
                    </button>
                </OeTip>
            ) : null}

            {changed && (cur === 'PREPAYMENT' || cur === 'ADVANCE_PAYMENT') ? (
                <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                    → „{curLabel}" wird beim Speichern gesetzt — löst die weclapp-
                    <b>{cur === 'PREPAYMENT' ? 'Vorkasse' : 'Anzahlungs'}</b>-Logik aus
                    {cur === 'PREPAYMENT'
                        ? ' (100 % im Voraus)'
                        : ' (Teilbetrag im Voraus, eigene Anzahlungsrechnung)'}
                    , danach nicht mehr rücknehmbar
                </span>
            ) : null}

            {changed && cur !== 'PREPAYMENT' && cur !== 'ADVANCE_PAYMENT' ? (
                <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                    → „{curLabel}" wird beim Speichern gesetzt
                </span>
            ) : null}

            {!changed && !needSet && p.suggested ? (
                <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>
                    ✓ passt zur Zahlungsbedingung
                </span>
            ) : null}

            {cur === 'ADVANCE_PAYMENT' ? (
                <div className="advbox">
                    <div className="advhead">
                        💶{' '}
                        {p.advancePercent != null ? (
                            <span>
                                Anzahlung laut Zahlungsbedingung: <b>{p.advancePercent}%</b>{' '}
                                <span className="muted">— Betrag übernehmen oder frei eintragen</span>
                            </span>
                        ) : (
                            <span>
                                Anzahlung{' '}
                                <span className="muted">
                                    — kein %-Satz in der Zahlungsbedingung erkennbar, bitte Betrag selbst eintragen
                                </span>
                            </span>
                        )}
                        <HelpTooltip id="oe-adv-help" placement="bottom" size={0.7}>
                            Der Prozentsatz wird aus dem Namen der Zahlungsbedingung gelesen. Der Betrag ist immer
                            frei eingebbar — der Vorschlag (% × Bruttobetrag) ist nur ein Angebot.
                        </HelpTooltip>

                        {(p.advanceSuggested != null && p.advanceSuggested !== eff && canEdit) ? (
                            <OeTip tip={'Übernimmt die vorgeschlagene Anzahlung (% × Bruttobetrag) in das Feld. '
                                    + 'Vorgemerkt — gespeichert wird erst mit „Speichern".'}>
                                <button
                                    type="button"
                                    className="actbtn"
                                    onClick={() => {
                                        setAdvText(fmt(p.advanceSuggested));
                                        setPending({advance: p.advanceSuggested});
                                    }}
                                >
                                    ✓ {p.advancePercent != null ? `${p.advancePercent}% = ` : ''}
                                    {fmt(p.advanceSuggested)} € übernehmen
                                </button>
                            </OeTip>
                        ) : null}

                        {advChanged ? (
                            <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                                → wird beim Speichern gesetzt
                            </span>
                        ) : (eff != null && p.advanceCurrent != null ? (
                            <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>✓ gesetzt</span>
                        ) : null)}
                    </div>

                    <div className="advsums">
                        <div className="advsum">
                            <span>Gesamt (brutto)</span>
                            <b>{fmt(gross)} €</b>
                        </div>

                        <div className="advsum hl">
                            <span>Anzahlung {effPct != null ? `(${effPct.toLocaleString('de-DE', {maximumFractionDigits: 1})} %)` : ''} ✎</span>
                            <input
                                ref={advanceRef}
                                className="advinput"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                disabled={!canEdit}
                                aria-label="Anzahlungsbetrag"
                                placeholder="Betrag eingeben…"
                                value={advText}
                                onChange={(e) => onAdvInput(e.target.value)}
                                onBlur={() => setAdvText(eff != null ? fmt(eff) : '')}
                            />
                        </div>

                        <div className="advsum">
                            <span>Restzahlung</span>
                            <b>{rest != null ? `${fmt(rest)} €` : '—'}</b>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
